import React, { useState, useLayoutEffect } from "react";
import PropTypes from "prop-types";
import { useLocation, useHistory } from "react-router-dom";
import { parse } from "query-string";

import { GetIconButton } from "./styles";
import KeepEditsWarning from "./KeepEditsWarning";
import IconDetailDrawer from "icons/IconDetailDrawer";
import IconDetailDownload from "icons/IconDetailDownload";

import { ICON_CHECKOUT } from "data/links";
import {
    useIconIsEdited,
    useIconEditorVisibility
} from "js/contexts/IconEditorContext";
import { UploadLicense } from "js/graphql";
import {
    QUERY_PURCHASED,
    PRO_SINGLE_DOWNLOAD,
    PRO_PREPAID_DOWNLOAD,
    FREE_DOWNLOAD,
    PRO_ANNUAL_DOWNLOAD
} from "icons/helpers";
import breakpoints from "core/breakpoints";
import { useViewport } from "js/hooks/useViewport";
import { useAuth } from "frontend/hooks/useAuth";

const STEP_GET_ICON = 1;
const STEP_SELECT_OPTION = 2;
const STEP_FREE = 3;
const STEP_PURCHASED = 4;

const IconDetailSteps = ({ icon }) => {
    const { id: iconId, licenseDescription: iconLicense } = icon;

    const history = useHistory();

    const queryString = useLocation().search;
    const { [QUERY_PURCHASED]: showPurchased } = parse(queryString);
    const initialStep =
        showPurchased === FREE_DOWNLOAD
            ? STEP_FREE
            : showPurchased
            ? STEP_PURCHASED
            : STEP_GET_ICON;
    const [step, setStep] = useState(initialStep);
    const [subscriptionSelected, setSubscriptionSelected] = useState(
        PRO_ANNUAL_DOWNLOAD
    );
    const [showEditWarningFor, setShowEditWarning] = useState(null);

    const { browserWidth } = useViewport();
    const onMobile = browserWidth < breakpoints.tablet.px;

    const hasEditedIcon = useIconIsEdited({ iconId });
    const {
        showControls,
        setShowControls,
        showAttribution,
        setShowAttribution,
        resetEditor
    } = useIconEditorVisibility();

    // To mitigate users bookmarking the download step, we remove the query from the URL.
    // (This uses the same pattern as PhotoDetailSteps; see for details)
    useLayoutEffect(() => {
        if (showPurchased) {
            history.replace(location.pathname);
        }
    }, []);

    const { isLoggedIn, confirmAuth, currentUser } = useAuth();
    const userHasSubscription = !!currentUser && currentUser.hasSubscription;

    const downloadTypes = {
        isPublic: iconLicense === UploadLicense.Publicdomain,
        isBasic: step === STEP_FREE,
        isPurchased: step === STEP_PURCHASED, // purchased subscription or single purchase
        isSinglePurchase: step === STEP_PURCHASED && !userHasSubscription, // includes prepaid
        isPro: userHasSubscription
    };
    const showDownloadView = Object.values(downloadTypes).some(value => value);

    // Show attribution if basic download
    // Hide editor controls if basic or ALC download
    useLayoutEffect(() => {
        if (downloadTypes.isBasic && !showAttribution) {
            setShowAttribution(true);
        } else if (!downloadTypes.isBasic && showAttribution) {
            setShowAttribution(false);
        }
        const editable =
            !downloadTypes.isBasic && !downloadTypes.isSinglePurchase;
        if (showControls && !editable) {
            setShowControls(false);
        } else if (!showControls && editable) {
            setShowControls(true);
        }
    }, [
        downloadTypes.isBasic,
        downloadTypes.isSinglePurchase,
        showControls,
        showAttribution
    ]);

    // Show download view -
    // existing subscription, purchased sub., purchased single, prepaid, basic, & public
    if (isLoggedIn && showDownloadView) {
        const onDownloadBack = () => {
            if (currentUser.hasPrepaidBalance && !currentUser.hasSubscription) {
                setStep(STEP_GET_ICON);
            } else {
                setStep(STEP_SELECT_OPTION);
            }
        };
        return (
            <IconDetailDownload
                {...downloadTypes}
                icon={icon}
                onBack={onDownloadBack}
            />
        );
    }

    return (
        <>
            <IconDetailDrawer
                show={step === STEP_SELECT_OPTION && isLoggedIn}
                icon={icon}
                hasEditedIcon={hasEditedIcon}
                onClose={() => setStep(STEP_GET_ICON)}
                setSubscriptionSelected={setSubscriptionSelected}
                onSubmit={checkoutType => {
                    if (
                        hasEditedIcon &&
                        !onMobile &&
                        (checkoutType === FREE_DOWNLOAD ||
                            checkoutType === PRO_SINGLE_DOWNLOAD)
                    ) {
                        setShowEditWarning(checkoutType);
                        setStep(STEP_GET_ICON);
                        return;
                    }
                    if (checkoutType === FREE_DOWNLOAD) {
                        setStep(STEP_FREE);
                        return;
                    }
                    // navigate to checkout page
                    // should be window vs history or else currentUser obj will reset causing editor to clear
                    window.location.assign(
                        ICON_CHECKOUT.replace(
                            ":checkoutType",
                            checkoutType
                        ).replace(":iconId", iconId)
                    );
                }}
            />

            <KeepEditsWarning
                show={!!showEditWarningFor}
                iconId={iconId}
                onClose={() => setShowEditWarning(null)}
                subscriptionSelected={subscriptionSelected}
                onLoseEditsClick={{
                    async onClick() {
                        if (showEditWarningFor === FREE_DOWNLOAD) {
                            setStep(STEP_FREE);
                            setShowEditWarning(null);
                        } else {
                            await resetEditor(); // wait for reset before navigating away
                            history.push(
                                ICON_CHECKOUT.replace(
                                    ":checkoutType",
                                    showEditWarningFor
                                ).replace(":iconId", iconId)
                            );
                        }
                    }
                }}
            />

            <GetIconButton
                isDisabled={step === STEP_SELECT_OPTION && isLoggedIn}
                onClick={() => {
                    confirmAuth({
                        onSuccess: authedCurrentUser => {
                            if (
                                authedCurrentUser.hasPrepaidBalance &&
                                !authedCurrentUser.hasSubscription
                            ) {
                                if (hasEditedIcon && !onMobile) {
                                    setShowEditWarning(PRO_PREPAID_DOWNLOAD);
                                } else {
                                    history.push(
                                        ICON_CHECKOUT.replace(
                                            ":checkoutType",
                                            PRO_PREPAID_DOWNLOAD
                                        ).replace(":iconId", iconId)
                                    );
                                }
                            } else {
                                setStep(STEP_SELECT_OPTION);
                            }
                        }
                    });
                }}
            >
                Get This Icon
            </GetIconButton>
        </>
    );
};

IconDetailSteps.propTypes = {
    icon: PropTypes.object.isRequired
};

export default IconDetailSteps;
